import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import {
  TbCheck,
  TbChevronRight,
  TbPlayerPlay,
  TbRefresh,
} from "react-icons/tb";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPlatformCreateAccess } from "../../../../Hooks/useBaasPlatformCreateAccess";
import { useToast } from "../../../../Hooks/toast";
import {
  isBaasHmlInstance,
  updateBaasInstance,
} from "../../../../Services/baasInstancesApi";
import { BAAS_HOMOLOGACAO_PATH } from "../constants";
import {
  areRequiredHomologStepsDone,
  countHomologProgress,
  getHomologStepSource,
  getHomologStepsForCapabilities,
  needsBaasHomologacao,
  resolveBaasHomologPair,
  shouldShowHomologWizardSteps,
} from "./baasHomologacao";
import { useHomologChecklist } from "./useHomologChecklist";

/**
 * Homologation wizard = instance client admin on HML (product-filtered).
 * Valut on Prod: primarily "Aprovar produção" (override allowed) — not the client checklist.
 * Steps never render on the Prod lane (fail-closed via shouldShowHomologWizardSteps).
 */
export const BaasHomologacaoPage = () => {
  const { addToast } = useToast();
  const {
    selectedInstance,
    instances,
    refreshInstances,
    selectInstance,
  } = useBaasInstance();
  const { capabilities, canViewProduction, canViewHomolog } = useBaasCapabilities();
  const { isValutConfigAdmin } = useBaasPlatformCreateAccess();

  const pair = useMemo(
    () => resolveBaasHomologPair(instances, selectedInstance),
    [instances, selectedInstance],
  );
  const production = pair.production;
  const development = pair.development;
  const viewingHml = shouldShowHomologWizardSteps(
    selectedInstance,
    instances,
  );
  const checklistInstance =
    development ||
    (isBaasHmlInstance(selectedInstance) ? selectedInstance : null);
  const checklistKey =
    checklistInstance?.id || checklistInstance?.baasInstanceId || null;

  const visibleSteps = useMemo(
    () => getHomologStepsForCapabilities(capabilities),
    [capabilities],
  );

  const { checks, sources, detecting, toggle, refresh } = useHomologChecklist({
    checklistKey,
    instance: checklistInstance,
    visibleSteps,
    // Probe HML twin whenever we have one (Prod gate needs progress too).
    enabled: Boolean(checklistInstance),
  });

  const [busyHml, setBusyHml] = useState(false);
  const [busyProd, setBusyProd] = useState(false);
  const [busyCreateHml, setBusyCreateHml] = useState(false);
  const [error, setError] = useState(null);

  const requiredDone = useMemo(
    () => areRequiredHomologStepsDone(checks, visibleSteps),
    [checks, visibleSteps],
  );
  const progress = useMemo(
    () => countHomologProgress(checks, visibleSteps),
    [checks, visibleSteps],
  );
  const hmlPending = Boolean(development && development.active === false);
  const hmlActive = Boolean(development && development.active !== false);
  const prodLiberated = production?.isProduction === true;
  const missingHml =
    Boolean(production) &&
    !(Number(production.hmlInstanceId) > 0) &&
    !development;
  const stillNeedsHomolog = needsBaasHomologacao(selectedInstance, instances);
  const canToggleChecks =
    viewingHml && Boolean(checklistInstance) && Boolean(checklistKey);
  const canActivateHml =
    viewingHml && isValutConfigAdmin && hmlPending && requiredDone;
  const canDeactivateHml =
    viewingHml && isValutConfigAdmin && hmlActive;
  /** Valut-only on Prod; checklist/HML active preferred but not required (manual override). */
  const canLiberateProd =
    !viewingHml &&
    isValutConfigAdmin &&
    Boolean(production) &&
    !prodLiberated;
  const isProdOverride =
    canLiberateProd && (!requiredDone || !hmlActive);

  if (!selectedInstance) {
    return (
      <div className="baas-homologacao min-w-0 space-y-4">
        <header>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.homologacao.title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.homologacao.noInstance")}
          </p>
        </header>
      </div>
    );
  }

  const handleActivateHml = async () => {
    if (!canActivateHml || !development || busyHml) return;
    setBusyHml(true);
    setError(null);
    try {
      await updateBaasInstance({ instance: development, active: true });
      addToast({
        type: "success",
        title: intl.get("baas.instances.hmlChecklist.approveSuccess"),
      });
      await refreshInstances();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.instances.hmlChecklist.approveError"),
      );
    } finally {
      setBusyHml(false);
    }
  };

  const handleDeactivateHml = async () => {
    if (!canDeactivateHml || !development || busyHml) return;
    const confirmed = window.confirm(
      intl.get("baas.instances.hmlChecklist.deactivateConfirm"),
    );
    if (!confirmed) return;
    setBusyHml(true);
    setError(null);
    try {
      await updateBaasInstance({ instance: development, active: false });
      addToast({
        type: "success",
        title: intl.get("baas.instances.hmlChecklist.deactivateSuccess"),
      });
      await refreshInstances();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.instances.hmlChecklist.deactivateError"),
      );
    } finally {
      setBusyHml(false);
    }
  };

  const handleLiberateProd = async () => {
    if (!canLiberateProd || !production || busyProd) return;
    if (isProdOverride) {
      const confirmed = window.confirm(
        intl.get("baas.homologacao.prodOverrideConfirm"),
      );
      if (!confirmed) return;
    }
    setBusyProd(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance: production,
        isProduction: true,
      });
      addToast({
        type: "success",
        title: intl.get("baas.homologacao.prodApproveSuccess"),
      });
      await refreshInstances();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.homologacao.prodApproveError"),
      );
    } finally {
      setBusyProd(false);
    }
  };

  const handleCreateHml = async () => {
    if (!isValutConfigAdmin || !production || busyCreateHml) return;
    setBusyCreateHml(true);
    setError(null);
    try {
      await updateBaasInstance({
        instance: production,
        createHmlTwin: true,
      });
      addToast({
        type: "success",
        title: intl.get("baas.instances.createHmlCard.success"),
      });
      await refreshInstances();
    } catch (err) {
      setError(
        err?.detail ||
          err?.message ||
          intl.get("baas.instances.createHmlCard.error"),
      );
    } finally {
      setBusyCreateHml(false);
    }
  };

  const switchToHml = () => {
    if (!canViewHomolog) return;
    if (development?.id) selectInstance?.(development.id);
  };

  const switchToProd = () => {
    if (!canViewProduction) return;
    if (production?.id) selectInstance?.(production.id);
  };

  const progressPct =
    progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  const hmlGateSection = viewingHml ? (
    <section className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface sm:p-5">
      <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">
        {intl.get("baas.homologacao.gatesTitle")}
      </h2>

      {isValutConfigAdmin && hmlPending ? (
        <button
          type="button"
          disabled={!canActivateHml || busyHml}
          onClick={handleActivateHml}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition enabled:hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <TbPlayerPlay size={16} />
          {busyHml
            ? intl.get("baas.instances.hmlChecklist.approving")
            : intl.get("baas.instances.hmlChecklist.approve")}
        </button>
      ) : null}

      {isValutConfigAdmin && hmlActive ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="button"
            disabled={!canDeactivateHml || busyHml}
            onClick={handleDeactivateHml}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 text-xs font-semibold text-amber-900 transition enabled:hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-800 dark:bg-transparent dark:text-amber-100 dark:enabled:hover:bg-amber-950/40"
          >
            {busyHml
              ? intl.get("baas.instances.hmlChecklist.deactivating")
              : intl.get("baas.instances.hmlChecklist.deactivate")}
          </button>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {intl.get("baas.instances.hmlChecklist.deactivateHint")}
          </p>
        </div>
      ) : null}

      {hmlPending && !requiredDone ? (
        <p className="text-[11px] text-amber-800 dark:text-amber-200">
          {intl.get("baas.homologacao.requiredHint")}
        </p>
      ) : null}

      {!isValutConfigAdmin ? (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.homologacao.partnerReadonlyGates")}
        </p>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.homologacao.valutHmlGateHint")}
        </p>
      )}
    </section>
  ) : null;

  const prodGateSection =
    !viewingHml && isValutConfigAdmin && !missingHml ? (
      <section className="space-y-3 rounded-2xl border border-[#e7a73f]/40 bg-[#e7a73f]/5 p-4 dark:border-[#e7a73f]/30 dark:bg-[#e7a73f]/10 sm:p-5">
        <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.homologacao.valutProdGatesTitle")}
        </h2>
        <p className="text-xs text-gray-600 dark:text-gray-400">
          {intl.get("baas.homologacao.valutProdGatesHint")}
        </p>

        <button
          type="button"
          disabled={!canLiberateProd || busyProd}
          onClick={handleLiberateProd}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition enabled:hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <TbCheck size={16} />
          {busyProd
            ? intl.get("baas.homologacao.prodApproving")
            : intl.get("baas.homologacao.prodApprove")}
        </button>

        {!prodLiberated ? (
          <p
            className={`text-[11px] ${
              isProdOverride
                ? "text-amber-800 dark:text-amber-200"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {intl.get(
              isProdOverride
                ? "baas.homologacao.prodOverrideHint"
                : "baas.homologacao.prodApproveHint",
            )}
          </p>
        ) : null}
      </section>
    ) : null;

  return (
    <div className="baas-homologacao mx-auto min-w-0 max-w-3xl space-y-6 pb-8">
      <header className="min-w-0">
        <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
          {intl.get("baas.homologacao.title")}
          {selectedInstance?.name ? (
            <span className="font-semibold text-gray-400 dark:text-gray-500">
              {" "}
              — {selectedInstance.name}
            </span>
          ) : null}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get(
            isValutConfigAdmin
              ? "baas.homologacao.subtitleValut"
              : "baas.homologacao.subtitle",
          )}
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {viewingHml ? (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
              hmlPending
                ? "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100"
                : hmlActive
                  ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                  : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
            }`}
          >
            {hmlPending
              ? intl.get("baas.instances.hmlChecklist.pendingBadge")
              : hmlActive
                ? intl.get("baas.instances.hmlChecklist.activeBadge")
                : intl.get("baas.homologacao.badgeHmlUnknown")}
          </span>
        ) : missingHml ? (
          <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-semibold text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
            {intl.get("baas.homologacao.badgeHmlMissing")}
          </span>
        ) : (
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
              prodLiberated
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                : "bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100"
            }`}
          >
            {prodLiberated
              ? intl.get("baas.homologacao.badgeProdLiberated")
              : intl.get("baas.homologacao.badgeProdBlocked")}
          </span>
        )}
        {!stillNeedsHomolog ? (
          <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100">
            {intl.get("baas.homologacao.badgeComplete")}
          </span>
        ) : null}
      </div>

      {(development || production) && (
        <div className="flex flex-wrap gap-2">
          {development ? (
            <button
              type="button"
              onClick={switchToHml}
              disabled={!canViewHomolog}
              title={
                canViewHomolog
                  ? undefined
                  : intl.get("baas.homologacao.switchForbiddenHml")
              }
              className={`inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                viewingHml
                  ? "border-[#e7a73f] bg-[#e7a73f]/10 text-[#a66f1d]"
                  : "border-gray-200 text-gray-600 hover:border-[#e7a73f] dark:border-brand-dark-border dark:text-gray-300"
              }`}
            >
              {intl.get("baas.homologacao.switchHml")}
            </button>
          ) : null}
          {production ? (
            <button
              type="button"
              onClick={switchToProd}
              disabled={!canViewProduction}
              title={
                canViewProduction
                  ? undefined
                  : intl.get("baas.homologacao.switchForbiddenProd")
              }
              className={`inline-flex h-8 items-center gap-1 rounded-lg border px-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                !viewingHml
                  ? "border-[#e7a73f] bg-[#e7a73f]/10 text-[#a66f1d]"
                  : "border-gray-200 text-gray-600 hover:border-[#e7a73f] dark:border-brand-dark-border dark:text-gray-300"
              }`}
            >
              {intl.get("baas.homologacao.switchProd")}
            </button>
          ) : null}
        </div>
      )}

      {missingHml ? (
        <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
          <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.instances.createHmlCard.title")}
          </h2>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {intl.get("baas.instances.createHmlCard.hint")}
          </p>
          {isValutConfigAdmin ? (
            <button
              type="button"
              disabled={busyCreateHml}
              onClick={handleCreateHml}
              className="mt-3 inline-flex h-9 items-center justify-center rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition enabled:hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyCreateHml
                ? intl.get("baas.instances.createHmlCard.creating")
                : intl.get("baas.instances.createHmlCard.action")}
            </button>
          ) : (
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
              {intl.get("baas.homologacao.waitValutHml")}
            </p>
          )}
        </section>
      ) : null}

      {/* Valut Prod first: liberate CTA — client wizard is secondary */}
      {prodGateSection}

      {/* Prod lane for partners: awaiting HML — not the step checklist */}
      {!viewingHml && !missingHml && !isValutConfigAdmin ? (
        <section className="rounded-2xl border border-amber-200/80 bg-amber-50/40 p-4 dark:border-amber-900/40 dark:bg-amber-950/20 sm:p-5">
          <h2 className="text-sm font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.homologacao.awaitingHmlTitle")}
          </h2>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {intl.get("baas.homologacao.awaitingHmlHint")}
          </p>
          {checklistKey ? (
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              {intl.get("baas.homologacao.progressLabel", {
                done: progress.done,
                total: progress.total,
              })}
              {progressPct > 0 ? ` · ${progressPct}%` : null}
            </p>
          ) : null}
          {development ? (
            <button
              type="button"
              onClick={switchToHml}
              disabled={!canViewHomolog}
              title={
                canViewHomolog
                  ? undefined
                  : intl.get("baas.homologacao.switchForbiddenHml")
              }
              className="mt-3 inline-flex h-9 items-center gap-1 rounded-lg bg-[#e7a73f] px-4 text-xs font-semibold text-white transition hover:bg-[#d4962f] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {intl.get("baas.homologacao.switchToHmlWizard")}
              <TbChevronRight size={14} />
            </button>
          ) : null}
        </section>
      ) : null}

      {/* Valut on Prod: optional link to inspect HML progress (not primary) */}
      {!viewingHml && !missingHml && isValutConfigAdmin && development ? (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          <button
            type="button"
            onClick={switchToHml}
            className="font-semibold text-[#a66f1d] hover:underline"
          >
            {intl.get("baas.homologacao.valutInspectHml")}
          </button>
          {checklistKey
            ? ` · ${intl.get("baas.homologacao.progressLabel", {
                done: progress.done,
                total: progress.total,
              })}`
            : null}
        </p>
      ) : null}

      {/* HML lane: full wizard for client admin; Valut can assist but copy says client-owned */}
      {viewingHml ? (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                {intl.get("baas.homologacao.progressLabel", {
                  done: progress.done,
                  total: progress.total,
                })}
              </p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {progressPct}%
                </p>
                <button
                  type="button"
                  onClick={() => refresh()}
                  disabled={detecting || !canToggleChecks}
                  className="inline-flex h-7 items-center gap-1 rounded-md border border-gray-200 px-2 text-[10px] font-semibold text-gray-600 transition enabled:hover:border-[#e7a73f] disabled:opacity-50 dark:border-brand-dark-border dark:text-gray-300"
                >
                  <TbRefresh
                    size={12}
                    className={detecting ? "animate-spin" : undefined}
                  />
                  {detecting
                    ? intl.get("baas.homologacao.detecting")
                    : intl.get("baas.homologacao.refreshDetect")}
                </button>
              </div>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className="h-full rounded-full bg-[#e7a73f] transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>
            <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
              {intl.get(
                isValutConfigAdmin
                  ? "baas.homologacao.flowHintValut"
                  : "baas.homologacao.flowHint",
              )}
            </p>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              {intl.get("baas.homologacao.autoDetectHint")}
            </p>
          </section>

          <ol className="space-y-3">
            {visibleSteps.map((step, index) => {
              const done = Boolean(checks[step.id]);
              const source = getHomologStepSource(sources, step.id);
              const label = intl.get(`baas.homologacao.steps.${step.id}`);
              const hint = intl.get(`baas.homologacao.steps.${step.id}Hint`);
              const canAuto = Boolean(step.detect);
              return (
                <li
                  key={step.id}
                  className={`rounded-2xl border p-4 transition dark:border-brand-dark-border ${
                    done
                      ? "border-emerald-200/80 bg-emerald-50/30 dark:bg-emerald-950/10"
                      : "border-gray-200 bg-white dark:bg-brand-dark-surface"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        done
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300"
                      }`}
                    >
                      {done ? <TbCheck size={16} /> : index + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex cursor-pointer items-start gap-2 text-sm font-semibold text-brand-dark dark:text-gray-100">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={done}
                            onChange={() => toggle(step.id)}
                            disabled={!canToggleChecks}
                          />
                          <span>{label}</span>
                        </label>
                        {!step.required ? (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            {intl.get("baas.homologacao.optional")}
                          </span>
                        ) : null}
                        {done && source === "auto" ? (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-800 dark:bg-sky-900/40 dark:text-sky-100">
                            {intl.get("baas.homologacao.sourceAuto")}
                          </span>
                        ) : null}
                        {done && source === "manual" ? (
                          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-medium text-violet-800 dark:bg-violet-900/40 dark:text-violet-100">
                            {intl.get("baas.homologacao.sourceManual")}
                          </span>
                        ) : null}
                        {!done && !canAuto ? (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                            {intl.get("baas.homologacao.manualOnly")}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {hint}
                      </p>
                      {step.href ? (
                        <Link
                          to={step.href}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#a66f1d] hover:underline"
                        >
                          {intl.get("baas.homologacao.goToStep")}
                          <TbChevronRight size={14} />
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>

          <p className="text-[10px] text-gray-400 dark:text-gray-500">
            {intl.get("baas.instances.hmlChecklist.saveLocal")}
          </p>
        </>
      ) : null}

      {hmlGateSection}

      {!viewingHml && !isValutConfigAdmin && !prodLiberated && !missingHml ? (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {intl.get("baas.homologacao.partnerAwaitingProd")}
        </p>
      ) : null}

      {error ? (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      ) : null}

      <p className="sr-only">{BAAS_HOMOLOGACAO_PATH}</p>
    </div>
  );
};
